import * as Yup from 'yup'

import { getBudgetValidation } from '../../../../Multiplatform/UploadAdCreative/UploadAdCreativeCampaignForm/fields'

import {
  END_DATE,
  endDateValidation,
  START_DATE,
  startDateValidation,
  startEndDateRangeInitialValues
} from '../../../../ReusableFormFields/StartEndDateRangeFields/fields'
import {
  AUDIENCES_OPTION,
  CUSTOM_AUDIENCES,
  customAudiencesInitialValues
} from '../../../../ReusableFormFields/Audiences/AudiencesFields/fields'
import {
  OPTION_CUSTOM,
  // OPTION_NO,
  OPTION_YES
} from '../../../../../constants/forms'
import { LINE_ITEM } from '../../../../Multiplatform/UploadAdCreative/UploadAdCreativeLineItemForm/fields'
import {
  BID_PRICE,
  PIXEL_EVENT,
  PIXEL_ID,
  pixelInitialValues
} from '../../../LineItemForms/LineItemTikTokCreate/LineItemTikTokCreateForm/fields'
import { DIGITAL_ALLOCATION_TYPE_BUDGET } from '../../../../Multiplatform/Partnerships/RightForms/fields'
import { IMPRESSIONS } from '../../../../Multiplatform/UploadAdCreative/fields'
import { getImpressionsValidation } from '../../../../Multiplatform/UploadAdCreative/ImpressionsBudgetSection/validation'
import {
  LIST_ITEM_OPTION,
  OPTION_EXISTING_LIST_ITEM,
  OPTION_NEW_LIST_ITEM
} from '../../../../../hooks/formHooks/listItemFormHooks/fields'
import {
  initialGeolocationsValues,
  SHOW_COUNTRIES,
  SHOW_OPTION,
  SHOW_OPTION_COUNTRIES
} from '../../../../ReusableFormFields/GeolocationsFields/fields'
import { BUDGET_DAILY, BUDGET_LIFETIME, BUDGET_OPTION } from '../../../../Multiplatform/fields'
import {
  AGE_18_24,
  AGE_25_34,
  AGE_35_44,
  AGE_45_54,
  AGE_55_100,
  CUSTOM_AGE_RANGES,
  customAgeRanges
} from '../../../../ReusableFormFields/AgeRangesFields/fields'
import { LANGUAGES } from '../../../../ReusableFormFields/LanguagesFields/fields'
import { LANGUAGE_VALUE } from '../../../../../constants/selectLists/languagesList'
import {
  FREQUENCY_DAYS,
  FREQUENCY_IMPRESSIONS,
  FREQUENCY_OPTION
} from '../../../../ReusableFormFields/FrequencyFields/fields'
import { CLICK, CONVERT, ENGAGED_VIEW, FOLLOWERS } from './optimizationGoalOptions'
import {
  ENGAGEMENT,
  LEAD_GENERATION,
  REACH,
  TRAFFIC,
  VIDEO_VIEWS,
  WEB_CONVERSIONS
} from '../../../../../constants/campaigns'
import { STANDARD_INVENTORY } from './brandSafetyOptions'
import { INTERESTS } from '../../../../ReusableFormFields/InterestsFields/fields'
import { INTEREST_KEYWORD_IDS } from './Steps/CustomAudiencesStep/TikTokUploadCreativeAudienceFields/TikTokAdvancedAudienceTargetingSection/AdditionalInterestCategoriesMultiselect'
import { PURCHASE_INTENTION_KEYWORD_IDS } from './Steps/CustomAudiencesStep/TikTokUploadCreativeAudienceFields/TikTokAdvancedAudienceTargetingSection/PurchaseIntentionCategoriesMultiselect'

export const BRAND_SAFETY = 'brand_safety'
export const OPTIMIZATION_GOAL = 'optimization_goal'
export const CATEGORY_EXCLUSION_IDS = 'category_exclusion_ids'
export const VERTICAL_SENSITIVITY_ID = 'vertical_sensitivity_id'

// Advanced settings for TikTok ad creative
export const COMMENT_DISABLED = 'comment_disabled'
export const SHARE_DISABLED = 'share_disabled'
export const VIDEO_DOWNLOAD_DISABLED = 'video_download_disabled'

const getInitialGoal = campaignObjective => {
  const campaignObjectiveLowerCase = campaignObjective?.toLowerCase()
  if (campaignObjectiveLowerCase === REACH) {
    return REACH
  } else if (campaignObjectiveLowerCase === TRAFFIC) {
    return CLICK
  } else if (campaignObjectiveLowerCase === VIDEO_VIEWS) {
    return ENGAGED_VIEW
  } else if (campaignObjectiveLowerCase === ENGAGEMENT) {
    return FOLLOWERS
  } else if (campaignObjectiveLowerCase === LEAD_GENERATION) {
    return CONVERT
  } else if (campaignObjectiveLowerCase === WEB_CONVERSIONS) {
    return CONVERT
  }
}

export const getInitialValues = ({
  isLineItemsExist,
  campaignObjective,
  shouldPopulateExistingValues,
  optimizationGoalFromExistingLineItem
}) => ({
  // line_item
  [LIST_ITEM_OPTION]: isLineItemsExist ? OPTION_EXISTING_LIST_ITEM : OPTION_NEW_LIST_ITEM,
  [LINE_ITEM]: '',
  // budget
  [BUDGET_OPTION]: BUDGET_LIFETIME,
  [BUDGET_DAILY]: '',
  [BUDGET_LIFETIME]: '',
  [IMPRESSIONS]: '',
  // dates
  ...startEndDateRangeInitialValues,
  // custom audiences
  ...customAudiencesInitialValues,
  // bid price
  [BID_PRICE]: '',
  // pixel
  ...pixelInitialValues,
  // GEOLOCATIONS
  ...initialGeolocationsValues,
  // ages
  [CUSTOM_AGE_RANGES]: {
    ...customAgeRanges,
    [AGE_18_24]: true,
    [AGE_25_34]: true,
    [AGE_35_44]: true,
    [AGE_45_54]: true,
    [AGE_55_100]: true
  },
  // languages
  [LANGUAGES]: [
    {
      label: 'English',
      value: LANGUAGE_VALUE.EN
    }
  ],
  // brand safety
  [BRAND_SAFETY]: STANDARD_INVENTORY,
  // frequency
  [FREQUENCY_OPTION]: OPTION_CUSTOM,
  [FREQUENCY_IMPRESSIONS]: '',
  [FREQUENCY_DAYS]: '',
  // optimization goal
  [OPTIMIZATION_GOAL]: shouldPopulateExistingValues
    ? optimizationGoalFromExistingLineItem
    : getInitialGoal(campaignObjective),
  [CATEGORY_EXCLUSION_IDS]: [],
  [VERTICAL_SENSITIVITY_ID]: '',
  [INTERESTS]: [],
  [INTEREST_KEYWORD_IDS]: [],
  [PURCHASE_INTENTION_KEYWORD_IDS]: [],
  // Advanced settings
  [COMMENT_DISABLED]: false,
  [SHARE_DISABLED]: false,
  [VIDEO_DOWNLOAD_DISABLED]: false
})

export const getValidationSchema = ({
  isBidTypeCustomPopulated,
  showImpressions,
  isCampaignLevelBudgetSet,
  allocationTypeOnDigitalRight,
  remainingBudget,
  remainingImpressions,
  currencySymbol,
  isCampaignObjectiveReach,
  campaignObjective,
  shouldPopulateExistingValues
}) => {
  const shouldValidateBidPrice =
    allocationTypeOnDigitalRight === DIGITAL_ALLOCATION_TYPE_BUDGET && isBidTypeCustomPopulated

  return Yup.object({
    // line item
    [LINE_ITEM]: Yup.string().when(LIST_ITEM_OPTION, {
      is: OPTION_EXISTING_LIST_ITEM,
      then: () => Yup.string().required('Please select line item')
    }),
    // budget
    ...(!isCampaignLevelBudgetSet &&
      !showImpressions && {
        [BUDGET_LIFETIME]: Yup.number().when([LIST_ITEM_OPTION, BUDGET_OPTION], {
          is: (listItemOption, budgetOption) => {
            return listItemOption === OPTION_NEW_LIST_ITEM && budgetOption === BUDGET_LIFETIME
          },
          then: () => getBudgetValidation({ remainingBudget, currencySymbol })
        }),
        [BUDGET_DAILY]: Yup.number().when([LIST_ITEM_OPTION, BUDGET_OPTION], {
          is: (listItemOption, budgetOption) => {
            return listItemOption === OPTION_NEW_LIST_ITEM && budgetOption === BUDGET_DAILY
          },
          then: () => getBudgetValidation({ remainingBudget, currencySymbol })
        })
      }),
    // dates
    [START_DATE]: Yup.string().when(LIST_ITEM_OPTION, {
      is: OPTION_NEW_LIST_ITEM,
      then: () => startDateValidation
    }),
    [END_DATE]: Yup.string().when(LIST_ITEM_OPTION, {
      is: OPTION_NEW_LIST_ITEM,
      then: () => endDateValidation
    }),
    ...(showImpressions && {
      [IMPRESSIONS]: Yup.number().when(LIST_ITEM_OPTION, {
        is: OPTION_NEW_LIST_ITEM,
        then: () => getImpressionsValidation({ remainingImpressions })
      })
    }),
    // custom audiences,
    [CUSTOM_AUDIENCES]: Yup.array().when(LIST_ITEM_OPTION, {
      is: OPTION_NEW_LIST_ITEM,
      then: () =>
        Yup.array().when(AUDIENCES_OPTION, {
          is: OPTION_YES,
          then: () => Yup.array().min(1, 'Please select at least one option')
        })
    }),
    ...(shouldValidateBidPrice && {
      [BID_PRICE]: Yup.number().when(LIST_ITEM_OPTION, {
        is: OPTION_NEW_LIST_ITEM,
        then: () => Yup.number().required('Please enter a bid price')
      })
    }),
    ...(!shouldPopulateExistingValues && {
      [PIXEL_ID]: Yup.string().when([LIST_ITEM_OPTION, OPTIMIZATION_GOAL], {
        is: (listItemOption, optimizationGoal) => {
          return (
            listItemOption === OPTION_NEW_LIST_ITEM &&
            ((campaignObjective?.toLowerCase() === LEAD_GENERATION && optimizationGoal === CONVERT) ||
              (campaignObjective?.toLowerCase() === WEB_CONVERSIONS && optimizationGoal === CONVERT))
          )
        },
        then: () => Yup.string().required('Please select pixel')
      }),
      [PIXEL_EVENT]: Yup.string().when([LIST_ITEM_OPTION, OPTIMIZATION_GOAL], {
        is: (listItemOption, optimizationGoal) => {
          return (
            listItemOption === OPTION_NEW_LIST_ITEM &&
            ((campaignObjective?.toLowerCase() === LEAD_GENERATION && optimizationGoal === CONVERT) ||
              (campaignObjective?.toLowerCase() === WEB_CONVERSIONS && optimizationGoal === CONVERT))
          )
        },
        then: () => Yup.string().required('Please select pixel event')
      })
    }),
    // GEOLOCATIONS
    [SHOW_COUNTRIES]: Yup.array().when([LIST_ITEM_OPTION, SHOW_OPTION], {
      is: (listItemOption, showOption) => {
        return listItemOption === OPTION_NEW_LIST_ITEM && showOption === SHOW_OPTION_COUNTRIES
      },
      then: () => Yup.array().min(1, 'Please select at least one option')
    }),
    // ages
    [CUSTOM_AGE_RANGES]: Yup.object().test({
      message: 'Select at least one age range',
      test: value => !!Object.keys(value).filter(item => value[item]).length
    }),
    // languages
    [LANGUAGES]: Yup.array().min(1, 'Please select at least one option'),
    // frequency
    ...(isCampaignObjectiveReach && {
      // REQUIREMENTS FROM SWAGGER:
      // frequency, together with frequency_schedule, controls how often people see your ad (only available for REACH ads).
      // Must meet the condition that 1 <= frequency <= frequency_schedule * 3 and 1 <= frequency_schedule <=7.
      // For example, frequency = 2 frequency_schedule = 3 means "show ads no more than twice every 3 day".
      [FREQUENCY_IMPRESSIONS]: Yup.number().when([LIST_ITEM_OPTION, FREQUENCY_OPTION], {
        is: (listItemOption, frequencyOption) => {
          return listItemOption === OPTION_NEW_LIST_ITEM && frequencyOption === OPTION_CUSTOM
        },
        then: () =>
          Yup.number()
            .required('Please enter frequency impressions')
            .min(1, 'Frequency must be at least 1')
            .test({
              name: 'frequency-max-validation',
              test: function (value) {
                const days = this.parent[FREQUENCY_DAYS]
                if (!days) return true
                return (
                  value <= days * 3 ||
                  this.createError({
                    message: `Frequency must be less than or equal to ${days * 3} (3 times the frequency schedule)`
                  })
                )
              }
            })
      }),
      [FREQUENCY_DAYS]: Yup.number().when([LIST_ITEM_OPTION, FREQUENCY_OPTION], {
        is: (listItemOption, frequencyOption) => {
          return listItemOption === OPTION_NEW_LIST_ITEM && frequencyOption === OPTION_CUSTOM
        },
        then: () =>
          Yup.number()
            .required('Please enter frequency days')
            .min(1, 'Frequency schedule must be at least 1')
            .max(7, 'Frequency schedule must be at most 7')
      })
    })
  })
}
